import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Box, Paper, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import StatWidget from '../widgets/StatWidget'
import ChartWidget from '../widgets/ChartWidget'
import Map from '../Map'
import { useAuth } from '../../contexts/AuthContext'
import {
  fetchStructureCount,
  fetchOccupancyData,
  fetchSeverityData,
  fetchDamageEstimateData,
} from '../../actions/fetchActions'

const occupancyColors = {
  AGRICULTURE: '#FFA500',
  INDUSTRIAL: '#4A90E2',
  COMMERCIAL: '#FFA500',
  GOVERNMENT: '#9B59B6',
  EDUCATION: '#E74C3C',
  ASSEMBLY: '#F1C40F',
  UNCLASSIFIED: '#95A5A6',
  RESIDENTIAL: '#2ECC71',
  'UTILITY AND MISC': '#E67E22',
  default: '#808080',
}

const severityColors = {
  MAJOR: '#FFA500',
  DESTROYED: '#FF0000',
  MINOR: '#FFFF00',
  MODERATE: '#32CD32',
  default: '#808080',
}

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`

const RichLoader = () => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      background:
        'linear-gradient(90deg, #f5f5f5 25%, #e0e0e0 50%, #f5f5f5 75%)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s infinite`,
      borderRadius: 2,
      boxSizing: 'border-box',
    }}
  />
)

const DashboardScreen = ({ selectedId, phase }) => {
  const { keycloak } = useAuth()
  const dispatch = useDispatch()
  const [structureCount, setStructureCount] = useState(null)
  const [occupancyData, setOccupancyData] = useState([])
  const [severityData, setSeverityData] = useState([])
  const [estimateData, setEstimateData] = useState([])
  const [loading, setLoading] = useState(true)
  const [drawnPolygon, setDrawnPolygon] = useState(null)

  const formatCurrency = (value) => {
    if (!value) return '$0'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  useEffect(() => {
    if (keycloak?.token && selectedId) {
      const fetchData = async () => {
        setLoading(true)
        try {
          // Use Promise.allSettled to capture individual results
          const results = await Promise.allSettled([
            dispatch(
              fetchStructureCount(
                selectedId,
                keycloak.token,
                drawnPolygon,
                phase
              )
            ),
            dispatch(
              fetchOccupancyData(
                selectedId,
                keycloak.token,
                drawnPolygon,
                phase
              )
            ),
            dispatch(
              fetchSeverityData(selectedId, keycloak.token, drawnPolygon, phase)
            ),
            dispatch(
              fetchDamageEstimateData(
                selectedId,
                keycloak.token,
                drawnPolygon,
                phase
              )
            ),
          ])

          let count = 0
          let rawOccupancyData = []
          let rawSeverityData = []
          let estimateDataCount = 0

          if (results[0].status === 'fulfilled') {
            count = results[0].value
          } else {
            console.error('Error in fetchStructureCount:', results[0].reason)
          }

          if (results[1].status === 'fulfilled') {
            rawOccupancyData = results[1].value
          } else {
            console.error('Error in fetchOccupancyData:', results[1].reason)
          }

          if (results[2].status === 'fulfilled') {
            rawSeverityData = results[2].value
          } else {
            console.error('Error in fetchSeverityData:', results[2].reason)
          }

          if (results[3].status === 'fulfilled') {
            estimateDataCount = results[3].value
          } else {
            console.error(
              'Error in fetchDamageEstimateData:',
              results[3].reason
            )
          }

          // Process Occupancy Data
          if (Array.isArray(rawOccupancyData)) {
            const formattedOccupancy = rawOccupancyData.map((item) => ({
              name: item['StructureDamagePolygon.occupancyClassification'],
              value: parseInt(
                item['StructureDamagePolygon.countDistinctBuilds'],
                10
              ),
              color:
                occupancyColors[
                  item['StructureDamagePolygon.occupancyClassification']
                ] || occupancyColors.default,
            }))
            setOccupancyData(formattedOccupancy)
          } else {
            setOccupancyData([])
          }

          // Process Severity Data
          if (Array.isArray(rawSeverityData)) {
            const formattedSeverity = rawSeverityData.map((item) => ({
              name: item[
                'IncidentDamageCategorySummary.damageCategoryDescription'
              ],
              value: parseInt(
                item['IncidentDamageCategorySummary.damageCount'],
                10
              ),
              color:
                severityColors[
                  item[
                    'IncidentDamageCategorySummary.damageCategoryDescription'
                  ]
                ] || severityColors.default,
            }))
            setSeverityData(formattedSeverity)
          } else {
            setSeverityData([])
          }

          setStructureCount(count)
          setEstimateData(formatCurrency(estimateDataCount))
        } catch (error) {
          console.error('Error in fetchData try block:', error)
          setStructureCount('0')
          setEstimateData('0')
          setOccupancyData([])
          setSeverityData([])
        } finally {
          setLoading(false)
        }
      }
      fetchData()
    }
  }, [dispatch, keycloak?.token, selectedId, drawnPolygon, phase])

  const handlePolygonDrawn = (polygon) => {
    if (polygon) {
      setDrawnPolygon(polygon)
    } else {
      setDrawnPolygon(null)
    }
  }

  if (!keycloak?.token) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          bgcolor: '#f5f5f5',
          boxSizing: 'border-box',
        }}
      >
        <Typography>Loading authentication...</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: '100%',
        bgcolor: '#f5f5f5',
        overflow: 'scroll',
        p: 1,
        boxSizing: 'border-box',
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'minmax(250px, 20%) 1fr minmax(250px, 20%)',
        },
        gridTemplateRows: { xs: 'auto 300px auto', md: '1fr' },
        gap: 2,
      }}
    >
      {/* Left Panel */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1,
            flex: 35,
            borderRadius: 2,
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            boxSizing: 'border-box',
            overflow: 'hidden',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            },
          }}
        >
          {loading ? (
            <RichLoader />
          ) : (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              <StatWidget
                title="Structure Impacts"
                value={structureCount}
                label="Structures"
                token={keycloak.token}
              />
            </Box>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 1,
            flex: 65,
            borderRadius: 2,
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            position: 'relative',
            boxSizing: 'border-box',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            },
          }}
        >
          {loading ? (
            <RichLoader />
          ) : (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                height: '100%',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              <ChartWidget
                title="Occupancy Type Distribution"
                data={occupancyData}
                token={keycloak.token}
              />
            </Box>
          )}
        </Paper>
      </Box>
      {/* Center Panel */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Map
          variable={selectedId}
          token={keycloak.token}
          onPolygonDrawn={handlePolygonDrawn}
          phase={phase}
        />
      </Paper>
      {/* Right Panel */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          boxSizing: 'border-box',
          height: '100%',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1,
            flex: 35,
            borderRadius: 2,
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            boxSizing: 'border-box',
            overflow: 'hidden',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            },
          }}
        >
          {loading ? (
            <RichLoader />
          ) : (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              <StatWidget
                title="Damage Estimate"
                value={estimateData}
                label="Total"
                token={keycloak.token}
              />
            </Box>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 1,
            flex: 65,
            borderRadius: 2,
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            position: 'relative',
            boxSizing: 'border-box',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            },
          }}
        >
          {loading ? (
            <RichLoader />
          ) : (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                height: '100%',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              <ChartWidget
                title="Damage Severity Distribution"
                data={severityData}
                token={keycloak.token}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  )
}

export default DashboardScreen
